import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import trimAccents from "util/trimAccents";
import { apiPayloadCreator } from "util/api";

const optionsSlices = {};

const multiInstanceOptions = [];

export function optionsSlice(name) {
  if (!optionsSlices[name]) {
    optionsSlices[name] = createOptionsSlice(name);
  }
  return optionsSlices[name];
}

function createOptionsSlice(name) {
  const hasInstances = multiInstanceOptions.includes(name);
  const instanceLoader = createAsyncThunk(
    "options/" + name + "/loader",
    apiPayloadCreator("", "get", (success, arg, thunkAPI) =>
      processOptions(name, success, arg, thunkAPI)
    )
  );
  const loader = hasInstances
    ? (arg) => (dispatch) => {
        const instanceArg = { ...arg };
        instanceArg.url = `${arg.baseUrl}/${arg.instance}`;
        dispatch(instanceLoader(arg));
      }
    : instanceLoader;

  const selector = (store) => store["options/" + name];

  const getInstance = (state, action) => {
    if (!hasInstances) return state;
    const arg = action.meta.arg;
    if (!state[arg.instance]) state[arg.instance] = {};
    return state[arg.instance];
  };

  const slice = createSlice({
    name: "options/" + name,
    initialState: hasInstances
      ? {}
      : {
          loaded: null,
          reloading: false,
          failed: false,
          loadArg: null,
          options: [],
          index: {},
        },

    extraReducers: {
      [loader.pending]: (state, action) => {
        const instance = getInstance(state, action);
        if (instance.loaded) {
          instance.reloading = true;
        } else {
          instance.loaded = false;
          instance.failed = false;
          instance.options = [];
          instance.index = {};
        }
        instance.loadArg = action.meta.arg;
      },
      [loader.fulfilled]: (state, action) => {
        const instance = getInstance(state, action);
        instance.loaded = true;
        instance.reloading = false;
        instance.failed = false;
        instance.options = action.payload.options;
        instance.index = action.payload.index;
      },

      [loader.rejected]: (state, action) => {
        const instance = getInstance(state, action);
        instance.reloading = false;
        if (instance.loaded && !instance.loadArg.reportReloadFailures) {
          console.error(action.payload);
          return;
        }
        instance.loaded = false;
        instance.failed = action.payload;
        instance.options = [];
        instance.index = {};
      },
    },
    reducers: {},
  });

  slice.selector = selector;
  slice.asyncActions = {
    loader: (arg) => (dispatch, getState) => {
      const state = selector(getState());
      const instance = state && hasInstances ? state[arg.instance] : state;
      if (!instance || instance.loaded === null) dispatch(loader(arg));
    },
    reload: (instanceId = null) => (dispatch, getState) => {
      const state = selector(getState());
      if (state) {
        const instance = hasInstances ? state[instanceId] : state;
        if ((instance.loaded || instance.failed) && !instance.reloading)
          dispatch(loader(instance.loadArg));
      }
    },
  };
  return slice;
}

export default optionsSlice;

function processOptions(name, apiResponse, loadArg, thunkAPI) {
  const optionData = apiResponse.items;
  const { optionConfig } = loadArg;

  const {
    labelField = "label",
    valueField = "value",
    aliases: aliasSpec = [],
    labelExtras: labelExtrasSpec = [],
  } = optionConfig;
  const options = optionData.map((item) => {
    const [label, asciiLabel] = trimAccents(item[labelField]);
    return {
      value: item[valueField],
      label: label,
      asciiLabel: asciiLabel,
      aliases: aliasSpec
        .map(([field, description]) => {
          const aliasStrings =
            field instanceof Function
              ? field(item)
              : item[field] == null
              ? []
              : Array.isArray(item[field])
              ? item[field]
              : [item[field]];
          return aliasStrings.map((string) => {
            const [aliasDisplay, aliasAscii] = trimAccents(string);
            return [description, aliasDisplay, aliasAscii];
          });
        })
        .flat(),
      labelExtras: labelExtrasSpec
        .map((func) => func(item))
        .flat()
        .filter((result) => result !== null),

      fullItem: item,
    };
  });
  const index = Object.fromEntries(
    options.map((option) => [option.value, option])
  );

  return { options, index };
}
